import logError from '@/utils/errors/logError';

import {
  AddressValidationRequest,
  AddressValidationResponse,
} from './models/AddressValidation';

const ADDRESS_VALIDATION_URL =
  'https://addressvalidation.googleapis.com/v1:validateAddress';

interface ValidateAddressParams {
  addressLines: string[];
  regionCode?: string;
}

/**
 * Validates a postal address using the Google Address Validation API.
 *
 * This service sends the provided address lines to Google's Address Validation
 * endpoint and returns a normalized and validated address response, including
 * deliverability and USPS CASS validation (for US addresses).
 *
 * The function requires a Google Address Validation API key to be configured
 * via the `VITE_GOOGLE_ADDRESS_VALIDATION_KEY` environment variable.
 *
 * @param params - Address validation parameters.
 * @param params.addressLines - One or more address lines (e.g. street, PO Box).
 * @param params.regionCode - ISO 3166-1 alpha-2 country code. Defaults to `'US'`.
 *
 * @returns A promise that resolves to an {@link AddressValidationResponse}
 * containing validation results, normalized address data, and metadata
 * returned by the Google Address Validation API.
 *
 * @throws {Error} If the Google Address Validation API key is missing.
 * @throws {Error} If the API request fails or returns a non-OK HTTP status.
 *
 * @remarks
 * - This function performs an external network request.
 * - Errors are logged internally via `logError` before being thrown.
 * - Consumers should handle thrown errors to provide user-friendly feedback.
 */
export const validateAddress = async ({
  addressLines,
  regionCode = 'US',
}: ValidateAddressParams): Promise<AddressValidationResponse> => {
  const apiKey = import.meta.env.VITE_GOOGLE_ADDRESS_VALIDATION_KEY;

  if (!apiKey) {
    logError(new Error('GoogleAddressValidation API Key missing'), {
      customTitle: 'GoogleAddressValidation API Key missing',
      fileName: 'AddressValidationService.ts',
      functionName: 'validateAddress',
    });
    throw new Error('Google Address Validation API key is missing');
  }

  if (!addressLines?.length) {
    throw new Error('At least one address line must be provided');
  }

  const requestBody: AddressValidationRequest = {
    address: {
      addressLines,
      regionCode,
    },
    enableUspsCass: true,
  };

  const response = await fetch(`${ADDRESS_VALIDATION_URL}?key=${apiKey}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    logError(
      new Error('GoogleAddressValidation API Failed to Validate address'),
      {
        customTitle: 'GoogleAddressValidation API Failed to validate address',
        fileName: 'AddressValidationService.ts',
        functionName: 'validateAddress',
        data: {
          status: response.status,
          statusText: response.statusText,
          addressLines,
          regionCode,
        },
      },
    );
    throw new Error('Failed to validate address');
  }

  return response.json();
};
